<template>
	<div v-if="visible" ref="root" class="_loading" :aria-label="title">
		<div class="g-row -p-small h-full align-center">
			<div ref="greeting" class="greeting g-col xxl-offset-2 flex m-c">
				<div class="icon">
					<span ref="iconSlot" class="relative flex m-t">
						<NavIcon name="burocratik" class="-show" />
						<NavIcon v-for="icon in icons" :key="icon" :name="icon" />
					</span>
				</div>
				<div class="message relative flex-grow-1">
					<div class="m-t">Hello</div>
					<div class="block whitespace-nowrap">
						<span class="m-t">We </span>
						<span class="m-t">are </span>
						<span class="m-t">Büro</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { gsap } from 'gsap'
import { nextTick, onBeforeUnmount, onMounted, ref, unref } from 'vue'
import { NavIcon } from '@/components/primitives/NavIcon'
import { getActiveContext } from '@/components/core'
import { lockBodyScroll } from '@/utils/navigation/bodyScrollLock'

defineProps({
	title: { type: String, default: 'Bürocratik Book' },
})

const icons = ['coffee', 'energy', 'gaming', 'heart', 'trophy', 'hands-horns', 'exploding-head']
const visible = ref(true)
const root = ref(null)
const greeting = ref(null)
const iconSlot = ref(null)

let unlockBody = null
let iconLoopInterval = null
let visibleIconIndex = 0

function viewportHeight(value) {
	if (typeof window === 'undefined') return 0
	return (window.innerHeight * value) / 100
}

function queryAll(selector, context = root.value) {
	return context ? [...context.querySelectorAll(selector)] : []
}

function waitForDomReady() {
	if (typeof document === 'undefined' || document.readyState !== 'loading') {
		return Promise.resolve()
	}

	return new Promise((resolve) => {
		document.addEventListener('DOMContentLoaded', resolve, { once: true })
	})
}

async function waitForActiveContext() {
	for (let i = 0; i < 60; i++) {
		const context = getActiveContext()

		if (context) return context

		await new Promise((resolve) => setTimeout(resolve, 50))
	}

	return null
}

function resetIconLoop() {
	const iconElements = queryAll('._icon', iconSlot.value)

	clearInterval(iconLoopInterval)
	iconLoopInterval = null

	iconElements[visibleIconIndex]?.classList.remove('-show')
	visibleIconIndex = 0
	iconElements[visibleIconIndex]?.classList.add('-show')
}

function startIconLoop() {
	const iconElements = queryAll('._icon', iconSlot.value)

	if (!iconElements.length) return () => {}

	iconLoopInterval = setInterval(() => {
		setTimeout(() => {
			iconElements[visibleIconIndex]?.classList.remove('-show')
			visibleIconIndex = (visibleIconIndex + 1) % iconElements.length
			iconElements[visibleIconIndex]?.classList.add('-show')
		}, Math.random() * 40)
	}, 260)

	return resetIconLoop
}

function greet() {
	return new Promise((resolve) => {
		const messageElements = queryAll('.message .m-t')
		const [hello, we, are, buro] = messageElements

		gsap.set(messageElements, { opacity: 1, yPercent: 120 })
		gsap.set(iconSlot.value, { opacity: 1, yPercent: -105 })

		const timeline = gsap.timeline({
			delay: 0.2,
			onComplete: () => {
				setTimeout(() => resolve(startIconLoop()), 300)
			},
		})

		timeline.to(hello, { duration: 0.6, ease: 'power2.out', yPercent: 0 }, 0)
		timeline.to(hello, { duration: 0.4, ease: 'power2.in', yPercent: -105 }, 0.6)
		timeline.to(
			[we, are, buro],
			{ duration: 0.5, stagger: 0.033, ease: 'power2.out', yPercent: 0 },
			1,
		)
		timeline.to(iconSlot.value, { duration: 0.6, ease: 'power2.out', yPercent: 0 }, 1)
	})
}

function resolvePageElement(pageContext) {
	return unref(pageContext?.element) ?? pageContext?.element ?? null
}

function dispatchReady(pageContext) {
	pageContext?.loader?.dispatch('ready')
	pageContext?.loader?.dispatch('transition-ready')
}

function enter({ pageContext, navbar, bot }) {
	const loadingElement = root.value
	const greetingElement = greeting.value
	const pageElement = resolvePageElement(pageContext)

	if (!loadingElement || !greetingElement) {
		dispatchReady(pageContext)
		return Promise.resolve()
	}

	const timeline = gsap.timeline({ delay: 0.2, paused: true })
	const done = timeline.then()

	if (pageElement) {
		timeline.set(pageElement, { transformOrigin: 'top' }, 0)
	}

	if (navbar && bot) {
		const botPosition = bot.getBoundingClientRect()
		const greetingPosition = greetingElement.getBoundingClientRect()
		const offset = {
			x: botPosition.x - greetingPosition.x,
			y: botPosition.y - greetingPosition.y,
		}

		timeline.set(navbar, { y: -1 * offset.y }, 0)

		if (offset.x > 5) {
			timeline.to(greetingElement, { duration: 0.7, ease: 'expo.inOut', x: offset.x }, 0)
		}

		timeline.to(greetingElement, { duration: 0.05, delay: 0.55, ease: 'power2.in', scale: 1 }, 0)
		timeline.to(
			loadingElement,
			{ duration: 0.95, ease: 'expo.inOut', y: viewportHeight(101) },
			0.15,
		)
		timeline.to(
			greetingElement.parentElement,
			{ duration: 0.95, ease: 'expo.inOut', y: viewportHeight(-101) },
			0.15,
		)
		timeline.to(navbar, { duration: 0.65, ease: 'expo.out', y: 0 }, 0.55)
		timeline.to(greetingElement, { duration: 0.65, ease: 'expo.out', y: offset.y }, 0.55)
	} else {
		timeline.to(greetingElement, { duration: 0.8, ease: 'power3.in', y: viewportHeight(-40) }, 0)
		timeline.to(
			loadingElement,
			{ duration: 0.95, ease: 'expo.inOut', y: viewportHeight(101) },
			0.15,
		)
	}

	if (pageElement && pageContext?.slug !== 'home') {
		timeline.from(
			pageElement,
			{ duration: 1.2, delay: 0.2, opacity: 0, ease: 'expo.out', y: viewportHeight(20) },
			0.15,
		)
	}

	timeline.add(() => dispatchReady(pageContext), 0.56)
	timeline.play()

	done.then(() => {
		gsap.set([pageElement, navbar].filter(Boolean), { clearProps: 'all' })
	})

	return done
}

async function run() {
	unlockBody = lockBodyScroll()

	await waitForDomReady()
	await nextTick()

	const pageContext = await waitForActiveContext()
	const navbar = document.body.querySelector('._navbar')
	const bot = navbar?.querySelector('._bot')
	const destroyGreet = await greet()

	if (pageContext?.loader?.loaded) {
		await pageContext.loader.loaded
	}

	destroyGreet?.()
	await enter({ pageContext, navbar, bot })

	unlockBody?.()
	unlockBody = null
	visible.value = false
}

onMounted(() => {
	run()
})

onBeforeUnmount(() => {
	resetIconLoop()
	unlockBody?.()
	gsap.killTweensOf(root.value)
	gsap.killTweensOf(greeting.value)
})
</script>

<style lang="scss" scoped>
.h-full {
	height: 100%;
}
.align-center {
	align-items: center;
}
.m-t {
	will-change: transform;
	display: inline-block;
	color: $white;
}
.relative {
	position: relative;
}
.whitespace-nowrap {
	white-space: nowrap;
	white-space: pre;
}
.flex-grow-1 {
	flex-grow: 1;
}
.flex {
	display: flex;
}

._loading {
	position: fixed;
	width: 100%;
	height: 100svh;
	left: 0;
	top: 0;

	z-index: 99999999;

	color: $white;
	background-color: hsl(0deg, 0%, 5%);
	overflow: hidden;

	cursor: wait;

	.greeting {
		display: flex;
		align-items: center;
		gap: 8px;
		padding-left: 2px;

		color: $white;
		overflow: hidden;

		transform: scale(1.333);
		transform-origin: 0% 0%;

		.icon :deep(._icon) {
			--size: 24px;
			--color: #{$white};

			position: absolute;
			inset: 0;

			opacity: 0;

			&:first-child {
				position: relative;
			}

			&.-show {
				opacity: 1;
			}
		}

		.message div {
			&:not(:first-child) {
				position: absolute;
				left: 0;
				top: 0;
			}
		}

		.m-t {
			opacity: 0;
		}
	}
}
</style>
